import { CommonHelperFunctions, IConfigLocalization, KeyCombinations } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import * as React from "react";
import { SEARCH_DEBOUNCE_MS, useDebouncedValue } from "../../../../shared/hooks/useDebouncedValue";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaHighlightText from "../../../ui-kit/kuika-highlight-text/kuika-highlight-text";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { ConfigPaneKey, findConfigCategory, IConfigCategory } from "./config-categories";
import { IConfigRevealTarget } from "./config-search-context";
import {
  buildConfigSearchHaystacks,
  IConfigSearchHaystack,
  IConfigSearchOptions,
  IConfigSearchPane,
  IConfigSearchRange,
  IConfigSearchRegistry,
  IConfigSearchResult,
  searchConfigEntries
} from "./config-search-index";
import "./config-search.scss";

/**
 * Finding one setting among the three hundred a configuration holds.
 *
 * The editor is deep on purpose — five platform panes, fifty-one categories, one category
 * drawn at a time — and that depth is exactly what makes a setting hard to find when the
 * reader knows what it is called but not where it lives. "Timeout" is three different
 * settings in three different panes; browser find reaches none of them, because every
 * category but the open one is `display: none`.
 *
 * So this searches the index the settings registered themselves into, and a result does not
 * merely name a place — it goes there: the pane's tab, that pane's rail, the row, and the
 * dialog if the match was on something only the dialog holds.
 *
 * Deliberately not a command palette. A palette is a modal over the whole app for jumping
 * anywhere; this is a field in the editor's own header, and the reader keeps the page they
 * were on. Esc closes the list and leaves them exactly where they were.
 */

export interface IConfigSearchProps {
  /** The index the mounted sections and fields registered into. */
  index: IConfigSearchRegistry;
  /** The panes the editor is drawing, in tab order. */
  panes: IConfigSearchPane[];
  /** Panes whose platform support is off — mounted and registered, but not reachable. */
  hiddenPanes: ConfigPaneKey[];
  /**
   * The descriptions the backend keys by string — `sd.configLocalizations`.
   *
   * The list itself rather than a resolver closure: a closure over it is a stable class
   * property, so nothing in the memo below would notice the descriptions arriving, and the
   * fields whose only searchable prose is a tooltip would stay unsearchable for the session.
   */
  localizations: IConfigLocalization[];
  /**
   * The query, owned by the container.
   *
   * The box is drawn inside the ACTIVE pane's shell, so it unmounts and remounts when the
   * platform tab changes — including the change a search result itself makes. The text has
   * to outlive that; the open list does not, and closing it is what choosing a result does
   * anyway.
   */
  query: string;
  onQueryChange: (query: string) => void;
  onReveal: (target: Omit<IConfigRevealTarget, "token">) => void;
}

interface IConfigSearchGroup {
  key: string;
  paneLabel: string;
  sectionTitle: string;
  /**
   * Where the query landed in the section's name, for the heading. Taken from the group's
   * best result — every result under one heading shares that name, so they share the marks.
   */
  sectionRanges: IConfigSearchRange[];
  icon?: IConfigCategory;
  /**
   * The section itself, when the query landed in its name or its description. It is drawn AS
   * the heading rather than as a row under it — the heading already names the place, and a
   * row repeating that name was the same restatement the breadcrumb used to be.
   */
  sectionResult?: IConfigSearchResult;
  /** The fields, in ranking order. */
  rows: IConfigSearchResult[];
}

const MIN_QUERY_LENGTH: number = 2;

/** Cmd on a Mac, Ctrl everywhere else — the glyph the rest of the designer prints. */
const modifierLabel = (): string => (navigator.platform?.toLowerCase().startsWith("mac") ? "⌘" : "Ctrl+");

/**
 * Results grouped the way the editor is laid out: one block per category, headed by the
 * pane it sits in. Ordering follows the ranking — a group appears where its best result
 * does — so the strongest match is always the first row of the first group.
 *
 * The heading is the only place the path is written. A row used to repeat it underneath its
 * own label, which put "App Settings › Connection Settings" twice in the same two inches.
 */
const groupResults = (results: IConfigSearchResult[], panes: IConfigSearchPane[]): IConfigSearchGroup[] => {
  const paneLabels: Map<ConfigPaneKey, string> = new Map<ConfigPaneKey, string>(
    panes.map((pane: IConfigSearchPane): [ConfigPaneKey, string] => [pane.key, pane.label])
  );
  const groups: Map<string, IConfigSearchGroup> = new Map<string, IConfigSearchGroup>();

  results.forEach((result: IConfigSearchResult): void => {
    const key: string = `${result.entry.pane}::${result.entry.categoryId ?? result.entry.sectionTitle}`;
    const group: IConfigSearchGroup = groups.get(key) ?? {
      key,
      paneLabel: paneLabels.get(result.entry.pane) ?? result.entry.pane,
      sectionTitle: result.entry.sectionTitle,
      sectionRanges: result.sectionRanges,
      icon: findConfigCategory(result.entry.pane, result.entry.categoryId),
      rows: []
    };
    groups.set(key, group);

    if (result.entry.kind === "section" && !group.sectionResult) {
      group.sectionResult = result;
      group.sectionRanges = result.sectionRanges;
      return;
    }
    group.rows.push(result);
  });

  return [...groups.values()];
};

export const ConfigSearch = ({
  index,
  panes,
  hiddenPanes,
  localizations,
  query,
  onQueryChange,
  onReveal
}: IConfigSearchProps) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [activeIndex, setActiveIndex] = React.useState<number>(0);
  const inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement> = React.useRef<
    HTMLInputElement | HTMLTextAreaElement
  >(null);

  // The index fills itself from ~300 mount effects. React 17 has no `useSyncExternalStore`,
  // so this is the counter the shell already uses for the same problem: a subscription that
  // bumps a version, and everything derived from the index keyed on it.
  const [indexVersion, setIndexVersion] = React.useState<number>(0);
  React.useEffect(
    (): (() => void) => index.subscribe((): void => setIndexVersion((version: number): number => version + 1)),
    [index]
  );

  const debouncedQuery: string = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const trimmedQuery: string = debouncedQuery.trim();
  const isSearching: boolean = trimmedQuery.length >= MIN_QUERY_LENGTH;

  // Keyed on the CONTENTS of the two lists, not on their identity. Both are rebuilt by the
  // container on every render, and the container re-renders on every keystroke anywhere in
  // the editor — so identity here would invalidate everything below it three hundred times
  // while someone fills in a text field.
  const paneKey: string = panes.map((pane: IConfigSearchPane): string => pane.key).join(",");
  const hiddenKey: string = hiddenPanes.join(",");
  const options: IConfigSearchOptions = React.useMemo((): IConfigSearchOptions => {
    const byKey: Map<string, string> = new Map<string, string>(
      localizations.map((localization: IConfigLocalization): [string, string] => [
        localization.key,
        localization.description ?? ""
      ])
    );
    return {
      panes,
      hiddenPanes,
      tooltipText: (key: string): string | undefined => byKey.get(key) || undefined
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paneKey, hiddenKey, localizations]);

  // Normalizing the index is per-character work over every label, description and tooltip it
  // holds — and it is not done at all until someone actually searches, so simply having the
  // editor open costs nothing.
  const haystacks: IConfigSearchHaystack[] = React.useMemo((): IConfigSearchHaystack[] => {
    if (!isSearching) return [];
    return buildConfigSearchHaystacks(index.entries(), options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, indexVersion, options, isSearching]);

  const results: IConfigSearchResult[] = React.useMemo((): IConfigSearchResult[] => {
    if (!isSearching) return [];
    return searchConfigEntries(haystacks, trimmedQuery, options);
  }, [haystacks, trimmedQuery, options, isSearching]);

  const groups: IConfigSearchGroup[] = React.useMemo(
    (): IConfigSearchGroup[] => groupResults(results, panes),
    [results, panes]
  );

  /**
   * The list as the arrows walk it — the same order it is drawn in, with each group's
   * heading in it when that heading is itself a result. `results` cannot serve: it is
   * ranking order, and grouping moves things.
   */
  const navigable: IConfigSearchResult[] = React.useMemo((): IConfigSearchResult[] => {
    const flat: IConfigSearchResult[] = [];
    groups.forEach((group: IConfigSearchGroup): void => {
      if (group.sectionResult) flat.push(group.sectionResult);
      group.rows.forEach((row: IConfigSearchResult): void => {
        flat.push(row);
      });
    });
    return flat;
  }, [groups]);

  const rootRef: React.RefObject<HTMLDivElement> = React.useRef<HTMLDivElement>(null);

  /**
   * Clicking anywhere else puts the list away.
   *
   * The panel is 560px over the rail and the top of the body, so without this the only ways
   * out were Escape and choosing something — and a reader who has changed their mind reaches
   * for neither. Rows call `preventDefault` on their own mousedown, so choosing one still
   * works: the row's click lands before this can act on it.
   */
  React.useEffect((): (() => void) | undefined => {
    if (!isOpen) return undefined;
    const onPointerDown = (event: MouseEvent): void => {
      const target: EventTarget | null = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return (): void => document.removeEventListener("mousedown", onPointerDown);
  }, [isOpen]);

  React.useEffect((): void => {
    setActiveIndex(0);
    activeId.current = undefined;
  }, [trimmedQuery]);

  /**
   * Keep the highlight on the RESULT it was on, not on the position it happened to be at.
   *
   * The list moves under a standing query: a dialog closing takes its fields out of the
   * index, the localizations arriving give every tooltip something to match and reorder
   * everything. Holding a position through that slides the highlight silently onto another
   * setting — and Enter then opens that one. So every deliberate move records which result
   * it landed on, and the repair below follows that result wherever it went.
   */
  const activeId: React.MutableRefObject<string | undefined> = React.useRef<string | undefined>(undefined);
  const moveTo = (index: number): void => {
    setActiveIndex(index);
    activeId.current = navigable[index]?.entry.id;
  };

  React.useEffect((): void => {
    if (navigable.length === 0) return;
    const wanted: number = activeId.current
      ? navigable.findIndex((result: IConfigSearchResult): boolean => result.entry.id === activeId.current)
      : -1;
    if (wanted === activeIndex) return;
    if (wanted !== -1) {
      setActiveIndex(wanted);
      return;
    }
    // The result is gone entirely — the list shrank under the query, or the query changed
    // and cleared the memory. Fall back to a position that at least exists.
    const clamped: number = Math.min(activeIndex, navigable.length - 1);
    setActiveIndex(clamped);
    activeId.current = navigable[clamped]?.entry.id;
    // Only when the LIST moved. Depending on `activeIndex` too would have this undo every
    // arrow press, by dragging the index back to the id it was about to leave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigable]);

  const isListVisible: boolean = isOpen && isSearching;

  const reveal = (result: IConfigSearchResult): void => {
    onReveal({
      id: result.entry.id,
      pane: result.entry.pane,
      categoryId: result.entry.categoryId,
      openDialog: result.openDialog
    });
    // The query stays: the reader may well want the next result from the same search, and
    // clearing the field would make them type it again.
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const clear = (): void => {
    onQueryChange("");
    setIsOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === "Escape") {
      // One Escape closes the list and keeps the query; a second clears it. The reader is
      // still on the page they were reading, which is the point of a field over a palette.
      if (isListVisible) {
        setIsOpen(false);
        return;
      }
      clear();
      inputRef.current?.blur();
      return;
    }

    if (event.key === "Enter") {
      // The header renders INSIDE the editor's antd form, so an Enter that gets away from
      // here submits the whole configuration. It is caught before the list is even
      // consulted: with two characters typed and no list open, saving is the last thing
      // the reader meant by it.
      event.preventDefault();
      event.stopPropagation();
      const chosen: IConfigSearchResult | undefined = isListVisible ? navigable[activeIndex] : undefined;
      if (chosen) reveal(chosen);
      return;
    }

    if (!isListVisible || navigable.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveTo((activeIndex + 1) % navigable.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveTo((activeIndex - 1 + navigable.length) % navigable.length);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      moveTo(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      moveTo(navigable.length - 1);
    }
  };

  /**
   * Ctrl/Cmd+F focuses the field.
   *
   * It is what the reader already reaches for, and browser find cannot answer it here: the
   * fifty categories it would search are not in the layout. Left alone inside a dialog,
   * though — a dialog IS all mounted and visible, so find works there and taking it away
   * would be a loss.
   */
  React.useEffect((): (() => void) => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (CommonHelperFunctions.getKeyCombinationsFromKeysForDesignWindows(event) !== KeyCombinations.ctrlF) return;
      const target: EventTarget | null = event.target;
      if (target instanceof HTMLElement && target.closest("[role='dialog'], .monaco-editor")) return;
      event.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
      setIsOpen(true);
    };

    window.addEventListener("keydown", onKeyDown, true);
    return (): void => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  const renderRow = (result: IConfigSearchResult, flatIndex: number): React.ReactNode => {
    const isActive: boolean = flatIndex === activeIndex;
    return (
      <div
        key={result.entry.id}
        id={`configSearchOption-${result.entry.id}`}
        role="option"
        aria-selected={isActive}
        className={classNames("kuika-config-search__row", { "kuika-config-search__row--active": isActive })}
        data-testid={`configSearchResult-${result.entry.id}`}
        ref={
          isActive
            ? (element: HTMLDivElement | null): void =>
                // Always `nearest`: `scrollIntoView` walks every scrollable ancestor, and
                // centring here would scroll the editor behind the list as well.
                element?.scrollIntoView({ block: "nearest" })
            : undefined
        }
        // The list closes on blur, and a click's mousedown fires first — so the row has to
        // take the selection before the field can lose focus.
        onMouseDown={(event: React.MouseEvent): void => event.preventDefault()}
        onMouseEnter={(): void => moveTo(flatIndex)}
        onClick={(): void => reveal(result)}
      >
        <span className="kuika-config-search__row-main">
          <KuikaHighlightText
            className="kuika-config-search__label"
            text={result.entry.label}
            ranges={result.labelRanges}
          />
          {result.openDialog && <KuikaBadge content="In dialog" color="grey" variant="subtle" size="small" />}
        </span>
        {result.snippet && (
          <KuikaSmallText className="kuika-config-search__snippet">
            <KuikaHighlightText text={result.snippet.text} ranges={result.snippet.ranges} />
          </KuikaSmallText>
        )}
      </div>
    );
  };

  let flatIndex: number = -1;

  return (
    <div className="kuika-config-search" ref={rootRef}>
      <KuikaInput
        ref={inputRef}
        size="md"
        className="kuika-config-search__input"
        placeholder="Search settings"
        value={query}
        role="combobox"
        aria-expanded={isListVisible}
        aria-controls="configSearchResults"
        aria-autocomplete="list"
        aria-activedescendant={
          isListVisible && navigable[activeIndex] ? `configSearchOption-${navigable[activeIndex].entry.id}` : undefined
        }
        aria-label="Search settings"
        data-testid="configSearchInput"
        leftIcon={<KuikaSvg name="search" width={14} height={14} />}
        rightIcon={
          query ? (
            <button
              type="button"
              className="kuika-config-search__clear"
              aria-label="Clear search"
              data-testid="configSearchClearButton"
              onMouseDown={(event: React.MouseEvent): void => event.preventDefault()}
              onClick={clear}
            >
              <KuikaSvg name="x" width={12} height={12} />
            </button>
          ) : (
            <span className="kuika-config-search__hint" aria-hidden="true">
              {`${modifierLabel()}F`}
            </span>
          )
        }
        onChange={(_event: React.ChangeEvent<HTMLInputElement>, value: string | number): void => {
          onQueryChange(String(value));
          setIsOpen(true);
        }}
        onFocus={(): void => setIsOpen(true)}
        onKeyDown={handleKeyDown}
      />

      {isListVisible && (
        <div className="kuika-config-search__panel" data-testid="configSearchPanel">
          <div
            className="kuika-config-search__list"
            id="configSearchResults"
            role="listbox"
            aria-label="Settings matching the search"
          >
            {groups.length === 0 ? (
              <KuikaEmptyState
                svgName="search-off"
                customName={`No settings match "${trimmedQuery}"`}
                description="Try the name of a setting, a section, or a word from its description."
              />
            ) : (
              groups.map((group: IConfigSearchGroup) => {
                // The heading is a result of its own when the query landed in the section's
                // name or its description; then it is what Enter opens, and it takes the
                // place in the arrow order its position on screen implies.
                const section: IConfigSearchResult | undefined = group.sectionResult;
                if (section) flatIndex += 1;
                const sectionIndex: number = flatIndex;
                const isSectionActive: boolean = section !== undefined && sectionIndex === activeIndex;

                return (
                  <div
                    className="kuika-config-search__group"
                    key={group.key}
                    role="group"
                    aria-label={`${group.paneLabel} ${group.sectionTitle}`}
                  >
                    <div
                      className={classNames("kuika-config-search__group-head", {
                        "kuika-config-search__group-head--result": section !== undefined,
                        "kuika-config-search__group-head--active": isSectionActive
                      })}
                      id={section ? `configSearchOption-${section.entry.id}` : undefined}
                      role={section ? "option" : undefined}
                      aria-selected={section ? isSectionActive : undefined}
                      data-testid={section ? `configSearchResult-${section.entry.id}` : undefined}
                      ref={
                        isSectionActive
                          ? (element: HTMLDivElement | null): void => element?.scrollIntoView({ block: "nearest" })
                          : undefined
                      }
                      onMouseDown={section ? (event: React.MouseEvent): void => event.preventDefault() : undefined}
                      onMouseEnter={section ? (): void => moveTo(sectionIndex) : undefined}
                      onClick={section ? (): void => reveal(section) : undefined}
                    >
                      <span className="kuika-config-search__group-path">
                        {group.icon && (
                          <KuikaSvg name={group.icon.icon} type={group.icon.iconType} width={14} height={14} />
                        )}
                        <span className="kuika-config-search__group-pane">{group.paneLabel}</span>
                        <KuikaHighlightText
                          className="kuika-config-search__group-name"
                          text={group.sectionTitle}
                          ranges={group.sectionRanges}
                        />
                      </span>
                      {section?.snippet && (
                        <KuikaSmallText className="kuika-config-search__snippet">
                          <KuikaHighlightText text={section.snippet.text} ranges={section.snippet.ranges} />
                        </KuikaSmallText>
                      )}
                    </div>
                    {group.rows.map((result: IConfigSearchResult) => {
                      flatIndex += 1;
                      return renderRow(result, flatIndex);
                    })}
                  </div>
                );
              })
            )}
          </div>

          {navigable.length > 0 && (
            <div className="kuika-config-search__footer" data-testid="configSearchFooter">
              <span>{navigable.length === 1 ? "1 setting" : `${navigable.length} settings`}</span>
              <span className="kuika-config-search__keys">
                <kbd>{"↑↓"}</kbd> move
                <kbd>{"↵"}</kbd> open
                <kbd>esc</kbd> close
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfigSearch;
